import { Avatar, AvatarFallback, AvatarImage } from '@ordo/ui/avatar'
import { cn } from '@ordo/ui/cn'
import type { ReactNode } from 'react'

import { type UserAvatarColor, userAvatarColorHex } from '@/lib/user-avatar-colors'

const AVATAR_SIZE_CLASS = {
  xs: 'h-5 w-5',
  sm: 'h-6 w-6',
  md: 'h-8 w-8',
  lg: 'h-14 w-14',
} as const

const FALLBACK_TEXT_CLASS = {
  xs: 'text-[9px]',
  sm: 'text-[10px]',
  md: 'text-xs',
  lg: 'text-base',
} as const

export interface UserAvatarProps {
  name: string
  initials: string
  avatarUri?: string | null
  avatarColor: UserAvatarColor
  size?: keyof typeof AVATAR_SIZE_CLASS
  className?: string
}

export function UserAvatar({ name, initials, avatarUri, avatarColor, size = 'sm', className }: UserAvatarProps) {
  return (
    <Avatar className={cn(AVATAR_SIZE_CLASS[size], 'shrink-0', className)}>
      {avatarUri ? <AvatarImage src={avatarUri} alt={name} /> : null}
      <AvatarFallback
        className={cn(FALLBACK_TEXT_CLASS[size], 'font-semibold text-white')}
        style={{ backgroundColor: userAvatarColorHex(avatarColor) }}>
        {initials}
      </AvatarFallback>
    </Avatar>
  )
}

export interface UserIdentityCardProps extends UserAvatarProps {
  subtitle?: ReactNode
  trailing?: ReactNode
  className?: string
  nameClassName?: string
  contentClassName?: string
}

export function UserIdentityCard({
  name,
  initials,
  avatarUri,
  avatarColor,
  size = 'sm',
  subtitle,
  trailing,
  className,
  nameClassName,
  contentClassName,
}: UserIdentityCardProps) {
  return (
    <div className={cn('flex min-w-0 items-center gap-2', className)}>
      <UserAvatar name={name} initials={initials} avatarUri={avatarUri} avatarColor={avatarColor} size={size} />
      <div className={cn('min-w-0 flex-1', contentClassName)}>
        <div className="flex min-w-0 items-center gap-1.5">
          <span className={cn('truncate text-sm font-medium text-foreground', nameClassName)}>{name}</span>
          {trailing}
        </div>
        {subtitle ? <div className="truncate text-xs text-muted-foreground">{subtitle}</div> : null}
      </div>
    </div>
  )
}
